import { useState } from "react";

import ExpandLessIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";
import TuneIcon from "@mui/icons-material/TuneRounded";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffRounded";
import VisibilityIcon from "@mui/icons-material/VisibilityRounded";
import { Box, Button, Collapse, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { FormikTextField } from "@shared";
import { FormikProps } from "formik";

import { SystemDropdownParameterDefinitionResponse, SystemDropdownParameterKind } from "@/api";
import { BooleanParameterValues } from "@/common/constants/systemDropdownParameterValues";
import { SystemDropdownsFormValues } from "@/pages/system-settings/tabs/system-dropdowns/SystemDropdownsSettings.types";
import {
  getSystemDropdownParameterLabel,
  getSystemDropdownParameterValueLabel,
} from "@/pages/system-settings/tabs/system-dropdowns/SystemDropdownsSettings.utils";
import { useTranslation } from "@/translations";

interface OptionRowProps {
  formik: FormikProps<SystemDropdownsFormValues>;
  index: number;
  maxValueLength?: number;
  parameterDefinitions: SystemDropdownParameterDefinitionResponse[];
  onToggleHidden: () => void;
}

const ParameterLabelWidth = 200;

/**
 * One value of a system-relevant dropdown. There is no delete action: an option is retired by hiding
 * it, which keeps its generated key and every reference to it intact. That key stays out of the UI —
 * it is an internal identifier with no meaning for the user.
 */
export function OptionRow({ formik, index, maxValueLength, parameterDefinitions, onToggleHidden }: OptionRowProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const row = formik.values.rows[index];
  const hasParameters = parameterDefinitions.length > 0;

  if (!row) {
    return null;
  }

  const setParameterValue = (parameterIndex: number, value: string) => {
    void formik.setFieldValue(`rows.${index}.parameters.${parameterIndex}.value`, value);
  };

  const allowedValuesFor = (definition: SystemDropdownParameterDefinitionResponse) =>
    definition.kind === SystemDropdownParameterKind.Boolean ? BooleanParameterValues : definition.allowedValues;

  return (
    <Box
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: row.isHidden ? "action.hover" : "transparent",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" px={1} pt={2} pb={0}>
        <FormikTextField
          formik={formik}
          path={`rows.${index}.value`}
          placeholder={t("settings.systemDropdowns.valuePlaceholder")}
          slotProps={{
            input: {
              inputProps: {
                maxLength: maxValueLength,
                "aria-label": t("settings.systemDropdowns.valueLabel", { position: index + 1 }),
              },
              // A hidden option stays editable; the strikethrough only signals it is out of use.
              sx: row.isHidden ? { textDecoration: "line-through", color: "text.disabled" } : undefined,
            },
          }}
        />

        {hasParameters && (
          <Button
            size="small"
            variant="outlined"
            aria-expanded={isExpanded}
            aria-label={t("settings.systemDropdowns.toggleParameters", { value: row.value })}
            startIcon={<TuneIcon />}
            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setIsExpanded((expanded) => !expanded)}
            sx={{ minWidth: 0, minHeight: 40, flexShrink: 0 }}
          >
            {parameterDefinitions.length}
          </Button>
        )}

        <IconButton
          aria-label={
            row.isHidden
              ? t("settings.systemDropdowns.showOption", { value: row.value })
              : t("settings.systemDropdowns.hideOption", { value: row.value })
          }
          onClick={onToggleHidden}
          sx={{ minWidth: 40, minHeight: 40, flexShrink: 0 }}
        >
          {row.isHidden ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </Stack>

      {hasParameters && (
        <Collapse in={isExpanded} unmountOnExit>
          <Stack gap={2} px={2} pt={1} pb={2} bgcolor="action.hover">
            <Typography variant="overline" color="text.secondary">
              {t("settings.systemDropdowns.additionalParameters")}
            </Typography>

            {parameterDefinitions.map((definition) => {
              const parameterIndex = row.parameters.findIndex((p) => p.parameter === definition.parameter);
              const parameterValue = row.parameters[parameterIndex]?.value;
              if (parameterValue === undefined) {
                return null;
              }

              const label = getSystemDropdownParameterLabel(definition.parameter, t);
              const labelId = `system-dropdown-parameter-${index}-${definition.parameter}`;

              return (
                <Stack key={definition.parameter} direction="row" alignItems="center" gap={1}>
                  <Typography id={labelId} variant="body2" minWidth={ParameterLabelWidth}>
                    {label}
                  </Typography>
                  <TextField
                    select
                    size="small"
                    sx={{ minWidth: 300 }}
                    slotProps={{ select: { labelId } }}
                    value={parameterValue}
                    onChange={(event) => setParameterValue(parameterIndex, event.target.value)}
                  >
                    {allowedValuesFor(definition).map((value) => (
                      <MenuItem key={value} value={value}>
                        {getSystemDropdownParameterValueLabel(value, t)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              );
            })}
          </Stack>
        </Collapse>
      )}
    </Box>
  );
}
